import React, { Fragment } from "react";
import OrderHistoryInvoiceSummary from "@/features/my-order/features/order-history/components/invoice-section/OrderHistoryInvoiceSummary";
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import OrderHistoryAccordion from "@/features/my-order/features/order-history/components/OrderHistoryAccordion";
import OrderHistoryButton from "@/features/my-order/features/order-history/components/OrderHistoryButton";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import { extractTransportationFromOrderHistory } from "@/features/my-order/features/order-history/utils";
import { toCamelKeyObject } from "@/utils/case";
import { TransportationStatus } from "@/features/transportations/transportation.type";
import TransportationStatusBadge from "@/features/my-order/components/TransportationStatusBadge";
import OrderHistoryTransportationTrackingModal from "@/features/my-order/features/order-history/components/transportation-section/OrderHistoryTransportationTrackingModal";
import { useState } from "react";
import { OrderHistoryClaimModal } from "@/features/my-order/features/order-history/features/claim";
import { useClaimModalStore } from "@/features/my-order/features/order-history/stores/useClaimModalStore";
import useGetClaimList from "@/features/my-order/features/order-history/features/claim/hooks/useGetClaimList";

function OrderHistoryTransportationContent() {
  const { tTitle, tSentence } = useI18nHelper();
  const { data } = useOrderHistoryProformaInvoice();
  const {
    isOpen: isTrackingModalOpen,
    onOpen: onTrackingModalOpen,
    onClose: onTrackingModalClose,
  } = useDisclosure();
  const { onOpen: onClaimModalOpen } = useClaimModalStore();
  const [variant, setVariant] = useState<
    "DOMESTIC" | "INTERNATIONAL" | "CUSTOMS"
  >("INTERNATIONAL");
  const [transportationId, setTransportationId] = useState<number>();

  const handleOpenTrackingModal = (
    variant: "DOMESTIC" | "INTERNATIONAL" | "CUSTOMS"
  ) => {
    setVariant(variant);
    onTrackingModalOpen();
  };
  const { data: claimList } = useGetClaimList();
  // const hasDelivered = data?.transportations.some(
  //   (transportation) => transportation.status === "DOMESTIC_SHIPPING_COMPLETED"
  // );

  return (
    <Flex flexDirection={"column"} gap={"16px"} paddingTop={"12px"}>
      {data?.transportations.map((transportation, index) => {
        const transportationDetail = extractTransportationFromOrderHistory(
          toCamelKeyObject(transportation)
        );
        const {
          name,
          receiverName,
          receiverContact,
          receiverAddress,
          status,
          houseBlNumber,
          domesticTrackingNumber,
          domesticCarrierName,
          mAwbNumber,
        } = transportationDetail;

        const hasHouseBlNumber = houseBlNumber && houseBlNumber?.length > 0;

        return (
          <Fragment key={transportation.id}>
            {index !== 0 && (
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            )}
            <OrderHistoryAccordion key={transportation.id}>
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <Flex
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      gap={"8px"}
                    >
                      <AccordionButton
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        cursor={"default"}
                        _hover={{
                          background: "none",
                        }}
                        padding={0}
                      >
                        <ToggleBar
                          isOpen={isExpanded}
                          name={name}
                          status={status}
                        />
                      </AccordionButton>
                      <Flex alignItems={"center"} gap={"12px"}>
                        <OrderHistoryButton
                          trailingIconSrc={"external-link"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTrackingModal("INTERNATIONAL");
                            setTransportationId(transportation.id);
                          }}
                          disabled={!mAwbNumber}
                        >
                          {tTitle(
                            "order_history:invoice.international_tracking"
                          )}
                        </OrderHistoryButton>
                        <OrderHistoryButton
                          trailingIconSrc={"external-link"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTrackingModal("CUSTOMS");
                            setTransportationId(transportation.id);
                          }}
                          disabled={!hasHouseBlNumber}
                        >
                          {tTitle("order_history:invoice.customs_status")}
                        </OrderHistoryButton>
                        <OrderHistoryButton
                          trailingIconSrc={"external-link"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTrackingModal("DOMESTIC");
                            setTransportationId(transportation.id);
                          }}
                          disabled={
                            !domesticTrackingNumber || !domesticCarrierName
                          }
                        >
                          {tTitle("order_history:invoice.domestic_tracking")}
                        </OrderHistoryButton>
                      </Flex>
                    </Flex>
                    <AccordionPanel padding={0}>
                      <Box padding={"8px 1px 12px 28px"}>
                        <Grid
                          padding={"16px"}
                          gap={"4px"}
                          templateColumns={"minmax(60px, max-content) 1fr"}
                          boxShadow={"0 0 0 0.5px #D4D4D1"}
                          borderRadius={"8px"}
                          alignItems={"center"}
                        >
                          <GridItem>
                            <Text
                              textStyle={"body-sm-regular"}
                              color={"#6F6F6C"}
                            >
                              {tTitle("order_history:invoice.receiver")}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text
                              textStyle={"body-md-regular"}
                              color={"#444444"}
                            >
                              {receiverName ?? "-"}
                            </Text>
                          </GridItem>

                          <GridItem>
                            <Text
                              textStyle={"body-sm-regular"}
                              color={"#6F6F6C"}
                            >
                              {tTitle("order_history:invoice.contact")}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text
                              textStyle={"body-md-regular"}
                              color={"#444444"}
                            >
                              {receiverContact ?? "-"}
                            </Text>
                          </GridItem>

                          <GridItem>
                            <Text
                              textStyle={"body-sm-regular"}
                              color={"#6F6F6C"}
                            >
                              {tTitle("order_history:invoice.address")}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text
                              textStyle={"body-md-regular"}
                              color={"#444444"}
                            >
                              {receiverAddress}
                            </Text>
                          </GridItem>
                        </Grid>
                      </Box>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            </OrderHistoryAccordion>
          </Fragment>
        );
      })}
      {!claimList ||
        (claimList?.claimProducts?.length === 0 && (
          <Flex
            padding={"4px 12px"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={"8px"}
          >
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {tSentence("order_history:claim.notification_due")}
            </Text>
            <Text
              textStyle={"body-sm-bold"}
              color={"#444444"}
              onClick={() => onClaimModalOpen()}
              cursor={"pointer"}
              _hover={{
                textDecoration: "underline",
              }}
            >
              {tSentence("order_history:claim.request_claim")}
            </Text>
          </Flex>
        ))}
      <OrderHistoryTransportationTrackingModal
        isOpen={isTrackingModalOpen}
        onClose={onTrackingModalClose}
        variant={variant}
        transportationId={transportationId}
      />
    </Flex>
  );
}

export default OrderHistoryTransportationContent;

interface TogglerBarProps {
  isOpen: boolean;
  name: string;
  status?: TransportationStatus;
}

const ToggleBar = ({ isOpen, name, status }: TogglerBarProps) => {
  return (
    <Flex
      alignItems={"center"}
      cursor={"pointer"}
      _hover={{
        background: "#F7F9FA",
      }}
      _active={{
        background: "#F0F0F0",
      }}
      paddingY={"4px"}
      flex={1}
    >
      <Flex alignItems={"center"}>
        <Box padding={"2px"}>
          <CustomIcon
            src={isOpen ? "caret-down" : "caret-right"}
            width={24}
            height={24}
            color={"#444444"}
          />
        </Box>
        <Flex alignItems={"center"} gap={"8px"}>
          <Text
            textStyle={"body-md-semi-bold"}
            color={"#363636"}
            noOfLines={1}
            maxWidth={"80%"}
            textAlign={"start"}
            wordBreak={"break-all"}
          >
            {name}
          </Text>
          <TransportationStatusBadge status={status} />
        </Flex>
      </Flex>
    </Flex>
  );
};
